import React, { useId, useState } from 'react';
import { NotificationPrivacy } from '../../types';
import { dbService } from '../../services/dbService';
import { useToast } from '../../contexts/ToastContext';

/**
 * 알림에 표시할 내용 수준 3지선다.
 *
 * 기기별 스위치인 NotificationSettingsPanel과 달리 **계정 전체** 설정이라 Tauri에서도 보인다
 * (그쪽 최상위 스위치는 트레이의 "알림 받기"지만, 내용 수준은 기기와 무관한 개인 설정이다).
 *
 * 라디오는 네이티브 <input type="radio">를 쓴다 — 화살표 키 이동·그룹 라벨링·스크린리더
 * 읽기가 전부 브라우저 기본 동작으로 따라온다. 직접 만든 role="radio" 목록은 그걸 다시
 * 구현해야 한다.
 */

interface Option {
  value: NotificationPrivacy;
  label: string;
  description: string;
}

const OPTIONS: readonly Option[] = [
  { value: 'full', label: '전체 표시', description: '보낸 사람과 메시지 내용을 모두 표시합니다' },
  { value: 'sender', label: '보낸 사람만', description: '누가 보냈는지만 표시하고 내용은 숨깁니다' },
  { value: 'hidden', label: '전부 숨김', description: '새 메시지가 왔다는 사실만 표시합니다' },
];

interface NotificationPrivacySettingProps {
  value: NotificationPrivacy;
  /** 서버 저장 성공 후 로컬 반영. App의 applyMyNotifyPrivacyLocally가 유일한 구현이다. */
  onChange: (privacy: NotificationPrivacy) => void;
}

export const NotificationPrivacySetting: React.FC<NotificationPrivacySettingProps> = ({
  value,
  onChange,
}) => {
  const groupName = useId();
  const { showToast } = useToast();
  // 저장 중에는 낙관적으로 먼저 칠한다 — 라디오는 즉시 반응하지 않으면 고장으로 보인다.
  // 실패하면 pending을 버려 서버가 아는 값(value)으로 되돌아간다.
  const [pending, setPending] = useState<NotificationPrivacy | null>(null);
  const [saving, setSaving] = useState(false);
  const shown = pending ?? value;

  const handleSelect = async (next: NotificationPrivacy) => {
    if (next === shown || saving) return;
    setPending(next);
    setSaving(true);
    try {
      await dbService.updateNotificationPrivacy(next);
      onChange(next);
    } catch {
      showToast({ type: 'error', message: '알림 설정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setPending(null);
      setSaving(false);
    }
  };

  return (
    <fieldset className="border-0 p-0 m-0 min-w-0">
      <legend className="text-sm font-semibold text-slate-800 mb-2">알림에 표시할 내용</legend>
      <div className="space-y-1">
        {OPTIONS.map((option) => {
          const checked = shown === option.value;
          return (
            <label
              key={option.value}
              className={
                checked
                  ? 'flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer bg-brand-100 transition-colors'
                  : 'flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors'
              }
            >
              <input
                type="radio"
                name={groupName}
                value={option.value}
                checked={checked}
                disabled={saving}
                onChange={() => void handleSelect(option.value)}
                className="mt-0.5 w-4 h-4 shrink-0 accent-brand-600"
              />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-slate-800">{option.label}</span>
                <span className="block text-xs text-slate-500 leading-relaxed">{option.description}</span>
              </span>
            </label>
          );
        })}
      </div>
      {/* 서버가 휴대폰 푸시에는 애초에 본문을 싣지 않는다. '전체 표시'를 골라도 폰에서는
          발신자까지만 보이므로, 그 차이를 미리 설명해 문의를 막는다. */}
      <p className="mt-2 px-3 text-xs text-slate-400 leading-relaxed">
        휴대폰 알림에는 원래 메시지 내용이 표시되지 않습니다
      </p>
    </fieldset>
  );
};
